import React from "react";
import { SeoHead } from "@/components/seo/SeoHead";
import { PageContainer } from "@/components/ui/PageContainer";
import { ContentLayout } from "@/components/content/ContentLayout";
import "@/styles/print/article-print.css";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sliders, CircleHelp, Flag } from "lucide-react";

const GettingStartedAlternatives: React.FC = () => {
  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title="Getting Started with Alternative Investments"
        description="Guide to access options (platforms, advisors, banks, liquid alts), readiness assessment, eligibility, due diligence, tax optimization, and practical next steps for Canadian investors."
        canonicalPath="/content/can/getting-started-alternative"
      />

      <header className="border-b border-border/60 bg-slate-900 text-slate-50">
        <PageContainer center>
          <div className="py-8 md:py-10">
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">
              Getting Started with Alternative Investments
            </h1>
            <p className="mt-2 text-slate-300 text-base md:text-lg">
              Understand access options, assess readiness, perform due diligence, and choose the right path for your situation.
            </p>
          </div>
        </PageContainer>
      </header>

      <main>
        <ContentLayout>
          <section className="prose prose-slate max-w-none py-8 prose-p:leading-7 prose-p:text-[15px]">
            <p>
              Alternative investments – private equity, real estate, private credit, hedge strategies, and more – represent a growing
              opportunity for Canadian investors to diversify portfolios and access returns uncorrelated with traditional stocks and bonds.
              But knowing where to start isn't obvious. Should you use platforms? Work with an advisor? Invest through your bank? Buy
              liquid alternative ETFs?
            </p>
            <p>
              This guide helps you navigate the landscape, assess your readiness, and choose the right access point for your situation.
            </p>

            <hr className="my-8 border-border/60 not-prose" />

            <h2>Understanding Your Access Options</h2>
            <p>Alternative investments aren't one-size-fits-all. Multiple access points exist, each with distinct tradeoffs:</p>

            <h3>Alternative Investment Platforms</h3>
            <p>
              Digital marketplaces operated by registered Exempt Market Dealers (EMDs). Examples include Hiive, FrontFundr, Parvis, addy,
              Fundscraper.
            </p>
            <p>
              <strong>Pros:</strong> Lower minimums ($100-$5,000), direct access, transparent fees, TFSA/RRSP eligibility on some offerings
              <br />
              <strong>Cons:</strong> Illiquid (3-7+ year commitments), requires hands-on due diligence, limited platform support
              <br />
              <strong>Best for:</strong> Self-directed investors comfortable with research and long lockups
            </p>
            <p>
              <strong><a href="/content/can/marketplace-guide">Explore Canadian alternative investment platforms →</a></strong>
            </p>

            <hr className="my-8 border-border/60 not-prose" />

            <h3>Wealth Advisors &amp; Private Banks</h3>
            <p>
              Access institutional alternatives through your advisor's firm. Most wealth managers run due diligence committees and offer a
              curated "shelf" of approved products – private equity funds, private credit, real estate funds, hedge fund-of-funds (pooled
              vehicles investing across multiple underlying funds).
            </p>
            <p>
              <strong>Pros:</strong> Professional guidance, institutional due diligence, integrated into portfolio management
              <br />
              <strong>Cons:</strong> Higher minimums ($25K-$250K+), limited selection varies by firm, additional fees, operational complexity
              (capital calls, complex tax reporting via T3s/T5013s), not all strategies available
              <br />
              <strong>Best for:</strong> Investors with significant capital seeking professional management and willing to accept curated access
            </p>
            <p>
              <strong>Key consideration:</strong> Your advisor's firm determines what's available. Even large platforms don't carry "most"
              institutional alternatives – only what their investment committees approve and have negotiated access to. Access typically
              requires accredited or permitted client status.
            </p>

            <hr className="my-8 border-border/60 not-prose" />

            <h3>Banks (Proprietary Private Funds)</h3>
            <p>
              Major Canadian banks offer proprietary alternative investment funds to qualifying clients. These are managed in-house or
              through strategic partnerships.
            </p>
            <p>
              <strong>Pros:</strong> Brand trust, integrated with existing banking relationships, regulatory oversight
              <br />
              <strong>Cons:</strong> Limited to bank's proprietary products, potential conflicts of interest, higher fees, capacity constraints
              <br />
              <strong>Best for:</strong> Investors prioritizing convenience and existing bank relationships over optimal selection
            </p>

            <hr className="my-8 border-border/60 not-prose" />

            <h3>Liquid Alternative ETFs &amp; Mutual Funds</h3>
            <p>Publicly-traded funds employing alternative strategies with daily liquidity. Available through any brokerage.</p>
            <p>
              <strong>Pros:</strong> Daily liquidity, low minimums, RRSP/TFSA eligible, regulatory transparency
              <br />
              <strong>Cons:</strong> Often diluted returns vs. true private alternatives, higher MERs than traditional ETFs, strategy constraints
              due to liquidity requirements
              <br />
              <strong>Best for:</strong> Investors wanting alternative exposure without illiquidity, or building familiarity before committing to
              private alternatives
            </p>

            <hr className="my-8 border-border/60 not-prose" />

            <h3>Angel Networks &amp; Accelerators</h3>
            <p>Direct angel investing through organizations like NACO, DMZ, Angel Investors Ontario.</p>
            <p>
              <strong>Pros:</strong> Direct deal flow, collective intelligence, mentorship opportunities, portfolio company involvement
              <br />
              <strong>Cons:</strong> $10K-$25K per deal, $1,500-$5,000+ annual fees, 5-10+ hours/month time commitment, 7-10 year illiquidity,
              echo chamber risks
              <br />
              <strong>Best for:</strong> Experienced operators with capital ($250K+ across 10-15 companies), time, and expertise to add value
              beyond money
            </p>
            <p>
              <strong><a href="/content/can/angel-networks-accelerators">Read our complete guide to angel networks →</a></strong>
            </p>

            <hr className="my-8 border-border/60 not-prose" />

            <h2>Choosing Your Access Point</h2>
            <p><strong>Choose based on:</strong></p>
            <ul>
              <li><strong>Capital available:</strong> Platforms work with $100-$5K. Advisors typically start at $25K-$250K per position.</li>
              <li><strong>Liquidity needs:</strong> Liquid alts for flexibility. Private alternatives only for capital you won't need for 5-10+ years.</li>
              <li><strong>Control vs. convenience:</strong> Platforms for control. Advisors for delegation.</li>
              <li><strong>Tax situation:</strong> TFSA/RRSP eligibility matters. Some platforms offer it; advisor products vary.</li>
              <li><strong>Knowledge &amp; time:</strong> Platforms require hands-on due diligence. Advisors provide curation.</li>
              <li><strong>Existing relationships:</strong> Banks/advisors convenient if you already work with them.</li>
            </ul>

            <hr className="my-8 border-border/60 not-prose" />

            <h2>Assessing Your Readiness</h2>
            <p>Before committing capital to alternatives, determine if they fit your situation:</p>

            <h3>Capital &amp; Liquidity</h3>
            <p>
              Alternative allocation varies widely. Institutional investors and pension funds often allocate 75%+ to alternatives. Higher
              allocations can work for retail investors who understand illiquidity and intend to leave capital invested 10+ years.
            </p>
            <p><strong>Critical questions:</strong></p>
            <ul>
              <li>Can you lock up this capital for 5-10+ years?</li>
              <li>Do you have adequate emergency funds and liquid assets elsewhere?</li>
              <li>Will you need this money for major expenses (home purchase, education, retirement) during the investment period?</li>
            </ul>
            <p>
              Never invest money you might need. Alternatives are illiquid by design – that's what enables potentially higher returns.
            </p>

            <h3>Knowledge &amp; Expertise</h3>
            <p>
              Alternatives are complex. Returns depend on manager skill, market timing, deal structure, and a dozen other factors most retail
              investors don't evaluate professionally.
            </p>
            <p><strong>Before investing:</strong></p>
            <ul>
              <li>
                Understand <a href="/content/can/modern-wealth-playbook#concepts">ownership vs. lending fundamentals</a>
              </li>
              <li>
                Learn <a href="/content/can/modern-wealth-playbook#alternatives">how private assets differ from public markets</a>
              </li>
              <li>Read offering memorandums cover to cover</li>
              <li>Understand fee structures (management + performance fees)</li>
              <li>Evaluate track records critically</li>
            </ul>
            <p>
              Education is the best investment you can make. While resources like the
              <a href="/content/can/modern-wealth-playbook"> Modern Wealth Playbook</a> consolidate research, exceptional resources exist
              across many sources – books, industry publications, courses, experienced advisors. Invest in your education broadly, from multiple
              perspectives.
            </p>

            <h3>Risk Tolerance</h3>
            <p>
              <a href="/tools/investor-quiz">Take the investor risk quiz</a> to understand your genuine risk capacity. Alternatives involve:
            </p>
            <ul>
              <li><strong>Illiquidity risk:</strong> Cannot exit positions easily</li>
              <li><strong>Valuation uncertainty:</strong> No daily pricing; valuations are estimates</li>
              <li><strong>Manager risk:</strong> Returns depend heavily on manager skill</li>
              <li><strong>Concentration risk:</strong> Individual positions can be large relative to portfolio</li>
              <li><strong>Loss of capital:</strong> Some investments will fail completely</li>
            </ul>
            <p>
              If daily market swings make you anxious, alternatives may actually suit you better (no daily pricing to obsess over). But if you
              need access to your money, stay liquid.
            </p>

            <hr className="my-8 border-border/60 not-prose" />

            <h2>Understanding Eligibility</h2>
            <p>
              Many alternative investments require <strong>accredited investor</strong> status or eligibility under{" "}
              <strong>Offering Memorandum (OM)</strong> exemptions.
            </p>

            <h3>Accredited Investor Status</h3>
            <p>Basic qualifications includes:</p>
            <ul>
              <li>Individual net income of $200K+ in each of the past two years (or $300K+ combined with spouse), with a reasonable expectation of the same in the current year</li>
              <li>Net financial assets of $1 million+ (alone or with spouse), before taxes and net of liabilities</li>
              <li>Net assets of $5 million+ (alone or with spouse)</li>
            </ul>
            <p>
              <a href="/tools/eligibility-test">Take the eligibility test</a> to understand which opportunities are available to you.
            </p>

            <h3>Offering Memorandum (OM) Exemptions</h3>
            <p>
              Some platforms use OM exemptions allowing non-accredited investors to participate with investment limits and suitability
              assessments by dealing representatives. Rules and investor protections vary by province.
            </p>
            <p>Understand which exemption applies to each investment. Different rules, different protections.</p>

            <hr className="my-8 border-border/60 not-prose" />

            <h2>Due Diligence Framework</h2>
            <p>
              Alternatives require hands-on due diligence. Don't rely solely on platform or advisor vetting.
            </p>

            <h3 className="inline-flex items-center gap-2"><CircleHelp className="h-5 w-5 text-amber-600" aria-hidden="true" /> Essential Questions</h3>
            <p><strong>About the Investment:</strong></p>
            <ul>
              <li>What's the strategy? How does it make money?</li>
              <li>What's the hold period? When can I expect liquidity?</li>
              <li>What are the fees? (Management + performance + other)</li>
              <li>What are the risks? Read risk disclosures carefully.</li>
              <li>What's the exit strategy? IPO? Acquisition? Sale?</li>
            </ul>
            <p><strong>About the Manager/Operator:</strong></p>
            <ul>
              <li>Track record? (Actual returns, not projections)</li>
              <li>Experience in this asset class?</li>
              <li>Skin in the game? (Do they invest personally?)</li>
              <li>References? (Talk to other investors)</li>
              <li>Regulatory history? (Check for enforcement actions)</li>
            </ul>
            <p><strong>About the Platform/Intermediary:</strong></p>
            <ul>
              <li>
                Properly registered? <a href="/glossary/provincial-regulators">Verify through provincial regulators</a>
              </li>
              <li>How do they earn revenue? (Fee structure clarity)</li>
              <li>Conflicts of interest? (Do they earn more on certain products?)</li>
              <li>Historical performance? (How have past deals performed?)</li>
            </ul>

            <h3 className="inline-flex items-center gap-2"><Flag className="h-5 w-5 text-red-600" aria-hidden="true" /> Red Flags</h3>
            <ul>
              <li>Guaranteed returns or "no risk" claims</li>
              <li>Pressure to invest quickly</li>
              <li>Lack of clear fee disclosure</li>
              <li>Manager won't answer questions directly</li>
              <li>No credible references</li>
              <li>Unregistered platforms or advisors</li>
              <li>Complex structures you don't understand</li>
            </ul>
            <p>If something feels off, walk away. There will always be another opportunity.</p>

            <hr className="my-8 border-border/60 not-prose" />

            <h2>Tax &amp; Account Optimization</h2>
            <p>Where you hold alternatives matters significantly for after-tax returns.</p>

            <h3>TFSA vs RRSP</h3>
            <p><strong>TFSA advantages:</strong></p>
            <ul>
              <li>Tax-free growth and withdrawals</li>
              <li>No impact on government benefits</li>
              <li>Flexibility to withdraw anytime (though alternatives themselves are illiquid)</li>
              <li>No mandatory withdrawals at any age</li>
            </ul>
            <p><strong>RRSP advantages:</strong></p>
            <ul>
              <li>Tax deduction on contribution (valuable if high income now, lower in retirement)</li>
              <li>Tax-deferred growth</li>
              <li>Potentially lower tax rate on withdrawal</li>
            </ul>
            <p>
              Not all alternative investments qualify for TFSA/RRSP. Platforms like FrontFundr, Equivesto, Parvis, and Fundscraper offer some
              RRSP/TFSA-eligible opportunities. Verify eligibility before investing.
            </p>
            <p>
              <a href="/content/can/modern-wealth-playbook#implementation">Learn more about tax-efficient account structuring →</a>
            </p>

            <h3>Tax Reporting Complexity</h3>
            <p>Private alternatives often involve complex tax reporting:</p>
            <ul>
              <li><strong>T3s</strong> (trust income)</li>
              <li><strong>T5013s</strong> (partnership income)</li>
              <li><strong>Capital calls</strong> (you commit capital but don't fund all at once)</li>
              <li><strong>Foreign tax credits</strong> (if investing in US/international deals)</li>
            </ul>
            <p>Work with accountants experienced in alternative investments. DIY tax software often can't handle these situations properly.</p>

            <hr className="my-8 border-border/60 not-prose" />

            <h2>Start Small, Diversify, Learn</h2>
            <p>The best approach to alternatives:</p>
            <p><strong>1. Start with small allocations</strong><br />Invest 5-10% of your portfolio initially. Build familiarity before scaling up.</p>
            <p><strong>2. Diversify across opportunities</strong><br />Don't concentrate in one deal, one platform, one asset class, one manager. Spread risk.</p>
            <p><strong>3. Document everything</strong><br />Track investments, capital calls, distributions, tax documents. Alternatives have long time horizons – good records matter.</p>
            <p><strong>4. Review regularly</strong><br />Check portfolio company updates quarterly. Reassess allocation annually. Platforms provide updates – read them.</p>
            <p><strong>5. Learn from mistakes</strong><br />Some investments will underperform or fail. That's normal. Learn what went wrong and refine your approach.</p>

            <hr className="my-8 border-border/60 not-prose" />

            <h2>Working with Professionals</h2>
            <h3>When to Use Advisors</h3>
            <p>Consider professional guidance when:</p>
            <ul>
              <li>Your net worth exceeds $500K and complexity warrants it</li>
              <li>You're navigating estate planning, trusts, intergenerational wealth transfer</li>
              <li>You have complex income streams (business ownership, stock options, real estate)</li>
              <li>You need behavioral coaching to avoid emotional decisions</li>
              <li>You want integrated financial planning beyond just investments</li>
            </ul>
            <h3>Choosing the Right Advisor</h3>
            <p>
              <strong>Fee-for-service advisors:</strong> Charge flat fees, hourly rates, or assets-under-management percentage. Fiduciary duty to
              act in your best interest. No product conflicts.
            </p>
            <p>
              <strong>Commission-based advisors:</strong> "Free" advice funded by commissions from products. Watch for conflicts – incentivized to
              sell what pays them, not necessarily what's optimal.
            </p>
            <p>
              Many advisors provide genuine value through comprehensive planning, tax optimization, and behavioral coaching. But for
              straightforward portfolios under $500K, the 1-2% annual drag often outweighs benefits when robo-advisors or DIY cost a
              fraction.
            </p>
            <p><strong>Questions to ask advisors:</strong></p>
            <ul>
              <li>How are you compensated?</li>
              <li>Are you a fiduciary?</li>
              <li>What alternatives do you have access to?</li>
              <li>What are the all-in fees (advisor + product)?</li>
              <li>Have you invested in these yourself?</li>
            </ul>

            <hr className="my-8 border-border/60 not-prose" />

            <h2>Build Your Complete Investment Strategy</h2>
            <p>
              Understanding alternatives is a journey, not a destination. Markets evolve. Regulations change. New opportunities emerge.
              Do-it-yourself (DIY) investing doesn't mean doing-it-alone. Explore the complete Modern Wealth Playbook for guidance on building diversified portfolios, understanding risk, and accessing private market opportunities through multiple channels.
            </p>
            <ul>
              <li><strong><a href="/content/can/modern-wealth-playbook">Modern Wealth Playbook →</a></strong></li>
              <li><strong><a href="/content/can/marketplace-guide">Alternative Investment Marketplaces →</a></strong></li>
            </ul>

            <div className="not-prose mt-6 md:mt-8 rounded-xl border border-dashed hover:border-solid border-muted-foreground/30 bg-gradient-to-br from-white to-muted/20 p-4 shadow-sm transition-shadow hover:shadow-xl">
              <div className="grid md:grid-cols-[1fr_auto] items-start gap-4">
                <div className="flex items-start gap-3 text-sm text-foreground">
                  <Sliders className="h-6 w-6 md:h-7 md:w-7 shrink-0 text-muted-foreground opacity-30" aria-hidden="true" />
                  <div>
                    <div className="font-medium">Build your custom allocation.</div>
                    <div className="text-muted-foreground">Use WealthKit to simulate different portfolio allocations tailored to your risk tolerance and goals.</div>
                  </div>
                </div>
                <Link to="/portfolio/tools" className="justify-self-end self-end">
                  <Button className="rounded-md bg-[#2D4C7F] hover:bg-[#243a66] text-white">Try WealthKit</Button>
                </Link>
              </div>
            </div>
          </section>
          <div className="not-prose text-[13px] text-muted-foreground mt-12 md:mt-16 mb-10">
            Educational content. Not individualized financial advice. We mention specific platforms for illustration purposes and receive no compensation for these references.
          </div>
          <div className="h-4" aria-hidden="true" />
        </ContentLayout>
      </main>
    </div>
  );
};

export default GettingStartedAlternatives;
